import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { emergencyServicesAPI } from '../../api/client';
import { INSTITUTION_TYPES, DEPENDENCY_TYPES, CARE_LEVELS } from '../../lib/emergencyConstants';
import { useAuth } from '../../context/AuthContext';
import { Plus, Search, X, Phone, Clock, MapPin, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';

function ServiceCard({ service, onView, onEdit, canEdit }) {
  const it = INSTITUTION_TYPES[service.institution_type] || INSTITUTION_TYPES.hospital;
  const dep = DEPENDENCY_TYPES[service.dependency_type] || DEPENDENCY_TYPES.publica;
  const addr = service.address || {};
  return (
    <div className={`soem-card hover:shadow-md transition-shadow overflow-hidden`} data-testid={`srv-card-${service.id}`}>
      <div className={`p-5 ${it.bg} border-b ${it.border}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{it.emoji}</span>
            <div>
              <p className={`text-xs font-mono ${it.text}`}>{service.service_code}</p>
              <h3 className="font-bold text-soem-navy text-base leading-tight mt-0.5">{service.name}</h3>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${dep.bg}`}>{dep.label}</span>
            {service.institution_type === 'hospital' && service.care_level && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-bold border border-blue-200">
                Nivel {service.care_level}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="p-4 space-y-2">
        {service.phone && (
          <p className="text-xs text-slate-600 flex items-center gap-1.5">
            <Phone size={11} className="text-slate-400" /> {service.phone}
          </p>
        )}
        <div className="flex items-center gap-4 text-xs text-slate-500">
          {service.estimated_response_time && (
            <span className="flex items-center gap-1"><Clock size={11} /> {service.estimated_response_time} min</span>
          )}
          {service.distance_km && (
            <span className="flex items-center gap-1"><MapPin size={11} /> {service.distance_km} km</span>
          )}
        </div>
        {addr.city && <p className="text-xs text-slate-400">{addr.city}</p>}
      </div>
      <div className="border-t border-slate-100 px-4 py-3 flex gap-2">
        <button onClick={() => onView(service.id)} className="soem-btn-primary flex-1 py-1.5 text-xs" data-testid={`view-${service.id}`}>Ver</button>
        {canEdit && <button onClick={() => onEdit(service.id)} className="soem-btn-secondary px-3 py-1.5 text-xs">Editar</button>}
      </div>
    </div>
  );
}

export default function EmergencyServicesList() {
  const navigate = useNavigate();
  const { isAdmin, isLider } = useAuth();
  const canEdit = isAdmin || isLider;

  const [services, setServices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: '', institution_type: '', dependency_type: '' });
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('emergency-services-view') || 'grid');

  const setView = (v) => {
    setViewMode(v);
    localStorage.setItem('emergency-services-view', v);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.institution_type) params.institution_type = filters.institution_type;
      if (filters.dependency_type) params.dependency_type = filters.dependency_type;
      const [srvRes, statsRes] = await Promise.all([
        emergencyServicesAPI.list(params),
        emergencyServicesAPI.stats(),
      ]);
      setServices(srvRes.data.items || []);
      setStats(statsRes.data);
    } catch { toast.error('Error al cargar servicios'); }
    finally { setLoading(false); }
  }, [filters.institution_type, filters.dependency_type]);

  useEffect(() => { load(); }, [load]);

  const filtered = filters.q
    ? services.filter(s => s.name.toLowerCase().includes(filters.q.toLowerCase()) || (s.address?.city || '').toLowerCase().includes(filters.q.toLowerCase()))
    : services;

  const hasFilters = filters.q || filters.institution_type || filters.dependency_type;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="emergency-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Servicios de Emergencia</h1>
          <p className="text-slate-500 text-sm mt-1">{services.length} servicios registrados</p>
        </div>
        {canEdit && (
          <button onClick={() => navigate('/dashboard/emergency-services/new')}
            className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="new-service-btn">
            <Plus size={16} /> Nuevo Servicio
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: 'bomberos', emoji: '🚒', label: 'Bomberos', color: '#DC2626' },
            { key: 'hospital', emoji: '🏥', label: 'Hospitales', color: '#1A56DB' },
            { key: 'policia', emoji: '🚔', label: 'Policía', color: '#0D1B2A' },
          ].map(s => (
            <div key={s.key} className="soem-card p-5 border-t-4" style={{ borderTopColor: s.color }}>
              <div className="flex items-center gap-3">
                <span className="text-3xl">{s.emoji}</span>
                <div>
                  <p className="soem-label">{s.label}</p>
                  <p className="text-3xl font-black text-soem-navy">{stats.by_institution_type?.[s.key] || 0}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="soem-card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
            placeholder="Buscar por nombre o ciudad..." aria-label="Buscar servicio de emergencia" value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} />
        </div>
        <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.institution_type} aria-label="Filtrar por tipo de institución" onChange={e => setFilters(f => ({ ...f, institution_type: e.target.value }))}>
          <option value="">Todos los tipos</option>
          {Object.entries(INSTITUTION_TYPES).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
        </select>
        <select className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
          value={filters.dependency_type} aria-label="Filtrar por dependencia" onChange={e => setFilters(f => ({ ...f, dependency_type: e.target.value }))}>
          <option value="">Todas las dependencias</option>
          {Object.entries(DEPENDENCY_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {hasFilters && (
          <button onClick={() => setFilters({ q: '', institution_type: '', dependency_type: '' })}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-soem-primary">
            <X size={14} /> Limpiar
          </button>
        )}
        {/* View toggle */}
        <div className="flex items-center border border-slate-300 rounded-sm overflow-hidden ml-auto">
          <button onClick={() => setView('grid')}
            className={`p-2 ${viewMode === 'grid' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
            title="Vista de cuadrícula">
            <LayoutGrid size={14} />
          </button>
          <button onClick={() => setView('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-soem-primary text-white' : 'text-slate-400 hover:bg-slate-50'}`}
            title="Vista de lista">
            <List size={14} />
          </button>
        </div>
      </div>

      {/* Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(3)].map((_, i) => <div key={i} className="soem-card h-48 animate-pulse bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="soem-card p-16 text-center">
          <span className="text-6xl block mb-4">🚒</span>
          <p className="text-slate-500 font-medium">
            {hasFilters ? 'No se encontraron servicios.' : 'No hay servicios de emergencia registrados.'}
          </p>
          {canEdit && !hasFilters && (
            <button onClick={() => navigate('/dashboard/emergency-services/new')}
              className="soem-btn-primary px-5 py-2 text-sm mt-4 inline-flex items-center gap-2">
              <Plus size={14} /> Crear servicio
            </button>
          )}
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(s => (
                <ServiceCard key={s.id} service={s} canEdit={canEdit}
                  onView={id => navigate(`/dashboard/emergency-services/${id}`)}
                  onEdit={id => navigate(`/dashboard/emergency-services/${id}/edit`)} />
              ))}
            </div>
          ) : (
            <div className="soem-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="soem-table-header">
                    <th className="px-4 py-3 text-left">Código</th>
                    <th className="px-4 py-3 text-left">Nombre</th>
                    <th className="px-4 py-3 text-left">Tipo</th>
                    <th className="px-4 py-3 text-left">Dependencia</th>
                    <th className="px-4 py-3 text-left">Teléfono</th>
                    <th className="px-4 py-3 text-left">Ciudad</th>
                    <th className="px-4 py-3 text-left">Distancia</th>
                    <th className="px-4 py-3 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => {
                    const it = INSTITUTION_TYPES[s.institution_type] || INSTITUTION_TYPES.hospital;
                    const dep = DEPENDENCY_TYPES[s.dependency_type] || DEPENDENCY_TYPES.publica;
                    return (
                      <tr key={s.id} className="soem-table-row" data-testid={`srv-row-${s.id}`}>
                        <td className="px-4 py-3 text-xs text-slate-400 font-mono">{s.service_code}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{it.emoji}</span>
                            <span className="font-semibold text-soem-navy">{s.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${it.bg} ${it.text}`}>
                            {it.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${dep.bg}`}>
                            {dep.label}
                          </span>
                          {s.institution_type === 'hospital' && s.care_level && (
                            <span className="ml-1 bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded font-bold">
                              Nivel {s.care_level}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{s.phone || '—'}</td>
                        <td className="px-4 py-3 text-slate-500">{s.address?.city || '—'}</td>
                        <td className="px-4 py-3 text-slate-500">{s.distance_km ? `${s.distance_km} km` : '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button onClick={() => navigate(`/dashboard/emergency-services/${s.id}`)}
                              className="text-soem-primary hover:underline text-xs font-semibold">Ver</button>
                            {canEdit && (
                              <button onClick={() => navigate(`/dashboard/emergency-services/${s.id}/edit`)}
                                className="text-slate-500 hover:underline text-xs">Editar</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
